import React, { useState, useMemo } from 'react';
import ReactVirtualizedTable from '../../../../SystemDesign/table'
import { 
  useSalonsMySalonsRetrieveQuery, 
  useTeamsTeamsListQuery 
} from '../../../../services/GLOWClient';
import { PrimaryButton, TitleScreen } from '../../../../SystemDesign/styles';
import { Button, Typography, CircularProgress, Alert } from '@mui/material';
import theme from '../../../../theme';
import AddTeam from './addTeam';
import DeleteTeam from './deleteTeam';
import UpdateTeam from './updateTeam';
import { useAuth } from '../../../../context/AuthContext';
import Can from '../../../../permissions/Can';

const Team = ({ salonId: propSalonId, onSuccess }) => {
    const { user } = useAuth();
  
  const [isAddTeamDialogOpen, setIsAddTeamDialogOpen] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const columns = [
    { label: 'Nom', dataKey: 'name' },
    { label: 'Description', dataKey: 'description' },
  ];

  // First fetch user's salons - only if no salonId prop provided
  const { data: salonsData, isLoading: salonsLoading } = useSalonsMySalonsRetrieveQuery(
    {}, { skip: !!propSalonId }
  );
  
  // Use prop salonId if provided, otherwise get the first salon ID
  const salonId = propSalonId || salonsData?.[0]?.salon_id || (Array.isArray(salonsData?.results) ? salonsData.results[0]?.salon_id : null);
  
  // Use teamsTeamsListQuery and filter by salonId
  const { data: teamsRawData, error, isLoading: teamsLoading, refetch: refetchTeams } = useTeamsTeamsListQuery(
    undefined,
    { skip: !salonId }
  );
  
  const isLoading = salonsLoading || teamsLoading;
  
  const handleAddSuccess = (message, result) => {
    refetchTeams();
    onSuccess && onSuccess(message, result);
  };

  const handleDeleteSuccess = () => {
    refetchTeams();
  };

  const handleUpdateSuccess = () => {
    refetchTeams();
  };

  const handleDelete = (row, index) => {
    setSelectedTeam(row);
    setIsDeleteDialogOpen(true);
  };

  const handleEdit = (row, index) => {
    setSelectedTeam(row);
    setIsUpdateDialogOpen(true);
  };

  const handleView = (row, index) => {
    // When viewing a team, we now have all the salon details available
  };

  // Transform the API response data to match the table format
  const data = useMemo(() => {
    if (!teamsRawData) {
      return [];
    }
    
    const teamsArray = Array.isArray(teamsRawData) 
      ? teamsRawData 
      : (teamsRawData.results ? teamsRawData.results : []);

    return teamsArray
      .filter(team => {
        const teamSalonId = typeof team.salon === 'object' ? team.salon.salon_id : team.salon;
        return Number(teamSalonId) === Number(salonId);
      })
      .map(team => {
        // Get salon name from the salons data
        const salonName = salonsData?.results?.[0]?.name || salonsData?.[0]?.name || 'Unknown Salon';
        
        return {
          id: team.team_id,
          name: team.name,
          salon_id: typeof team.salon === 'object' ? team.salon.salon_id : team.salon,
          salon_name: salonName,
          description: team.description || '-'
        };
      });
  }, [teamsRawData, salonsData, salonId]);

  

  return (
    <>
      <div style={{ padding: '24px' }}>
        {/* Header Section with proper spacing */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '32px',
          gap: '16px',
          flexWrap: 'wrap'
        }}>
          <Typography sx={TitleScreen(theme.colors)}>
            Gestion de groupe
          </Typography>
          
          <Can I="create" a="teams">
          <Button
            onClick={() => setIsAddTeamDialogOpen(true)}
            style={{
              ...PrimaryButton,
              backgroundColor: theme.colors.secondary[500],
              fontSize: '14px',
              padding: '8px 20px',
              height: '40px',
              borderRadius: '20px',
              whiteSpace: 'nowrap',
              minWidth: '140px',
              textTransform: 'none',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontWeight: '500',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Ajouter groupe
          </Button>
          </Can>
        </div>

        {/* Content Section with proper spacing */}
        <div style={{ marginTop: '24px' }}>
          {isLoading ? (
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center',
              padding: '60px 0',
              color: theme.colors.text.secondary
            }}>
              Loading teams...
            </div>
          ) : error ? (
            <div style={{ 
              padding: '20px',
              backgroundColor: theme.colors.error?.light || '#ffebee',
              color: theme.colors.error?.main || '#d32f2f',
              borderRadius: '8px',
              border: `1px solid ${theme.colors.error?.main || '#d32f2f'}`,
              marginBottom: '24px'
            }}>
              Error loading data: {error?.message}
            </div>
          ) : (
            <div style={{ 
              backgroundColor: '#fff',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              overflow: 'hidden'
            }}>
              <ReactVirtualizedTable 
                data={data}
                columns={columns}
                height={400}
                width="100%"
                showActions={true}
                onView={handleView}
                onEdit={(row, index) => {
                  // Only pass the action if user has permission
                  // The Can component can't be used directly in props, so we check permissions here
                  handleEdit(row, index);
                }}
                onDelete={(row, index) => {
                  // Only pass the action if user has permission
                  // The Can component can't be used directly in props, so we check permissions here
                  handleDelete(row, index);
                }}
                actionsWidth={120}
                renderActions={(row, index) => (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <Can I="view" a="teams">
                      <Button 
                        size="small" 
                        onClick={() => handleView(row, index)}
                        style={{ minWidth: '30px', padding: '4px' }}
                      >
                        View
                      </Button>
                    </Can>
                    <Can I="update" a="teams">
                      <Button 
                        size="small" 
                        onClick={() => handleEdit(row, index)}
                        style={{ minWidth: '30px', padding: '4px' }}
                      >
                        Edit
                      </Button>
                    </Can>
                    <Can I="delete" a="teams">
                      <Button 
                        size="small" 
                        onClick={() => handleDelete(row, index)}
                        style={{ minWidth: '30px', padding: '4px', color: theme.colors.error?.main || '#d32f2f' }}
                      >
                        Delete
                      </Button>
                    </Can>
                  </div>
                )}
              />
            </div>
          )}
        </div>
      </div>

      {/* Dialogs */}
      <AddTeam 
        open={isAddTeamDialogOpen} 
        onClose={() => setIsAddTeamDialogOpen(false)} 
        onSuccess={handleAddSuccess}
        selectedSalonId={salonId}
      />

      {selectedTeam && (
        <>
          <Can I="update" a="teams">
            <UpdateTeam 
              open={isUpdateDialogOpen} 
              onClose={() => setIsUpdateDialogOpen(false)}
              team={selectedTeam}
              onSuccess={handleUpdateSuccess}
            />
          </Can>
          
          <Can I="delete" a="teams">
            <DeleteTeam 
              open={isDeleteDialogOpen} 
              onClose={() => setIsDeleteDialogOpen(false)}
              team={selectedTeam}
              onSuccess={handleDeleteSuccess}
            />
          </Can>
        </>
      )}
    </>
  );
};

export default Team;